import { useInfiniteQuery } from '@tanstack/react-query'
import { useDebounce } from './useDebounce'
import { searchUsers, type UserSearchResponse } from '@/api/shares'
import { queryKeys } from '@/lib/queryKeys'

interface UseUserSearchParams {
  query: string
  debounceMs?: number
}

/**
 * Hook for searching users with debounced query and infinite scroll pagination
 * Only enables when query length >= 2
 */
export function useUserSearch({
  query,
  debounceMs = 300,
}: UseUserSearchParams) {
  const debouncedQuery = useDebounce(query, debounceMs)

  return useInfiniteQuery<UserSearchResponse>({
    queryKey: queryKeys.userSearch.byQuery(debouncedQuery),
    queryFn: ({ pageParam = 0 }) =>
      searchUsers({
        search: debouncedQuery,
        skip: pageParam as number,
        limit: 20,
      }),
    getNextPageParam: lastPage =>
      lastPage.hasMore ? lastPage.nextOffset : undefined,
    initialPageParam: 0,
    enabled: debouncedQuery.length >= 2,
    staleTime: 1000 * 60, // Cache for 1 minute
  })
}
